import React, { useEffect, useState, useRef } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grid,
    IconButton,
    Menu,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
} from '@mui/material';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { api } from 'src/_cores/_api/api';
import { SALES_API_URL } from 'src/_cores/_enums/api-url.enum';
import { DateTimeHelper } from 'src/_shares/_helpers/datetime-helper';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { ProductHelper } from 'src/_shares/_helpers/product-helper';
import { IShop } from 'src/_cores/_interfaces/user.interface';
import UserService from 'src/_cores/_services/user.service';
import { useAuthStore } from 'src/_cores/_store/root-store';
import InventoryService from 'src/_cores/_services/inventory.service';
import { IBrand } from 'src/_cores/_interfaces/inventory.interface';
import { get } from 'lodash';
import { useDebounce } from 'src/_cores/_hooks/search-debounce';
import ProductService from 'src/_cores/_services/product.service';
import { IProduct } from 'src/_cores/_interfaces/product.interface';
import { IUserGetParam } from '../../interfaces/user-interface';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

export default function DiscountList() {
    const [filterUsers, setFilterUsers] = useState<any[]>([]);
    const [discounts, setDiscounts] = useState([]);
    const authStore = useAuthStore();
    const [shops, setShops] = useState<IShop[]>([]);
    const [brands, setBrands] = useState<IBrand[]>([]);
    const [products, setProducts] = useState<IProduct[]>([]);
    const [users, setUsers] = useState<any[]>([]);
    const [open, setOpen] = useState(false);
    const [delAnchorEl, setDelAnchorEl] = useState<null | HTMLElement>(null);
    const openDel = Boolean(delAnchorEl);
    const [formData, setFormData] = useState({
        discountId: -1,
        discountCode: '',
        discountValue: '',
        discountStock: '',
        discountOriginalStock: 0,
        startDate: '',
        endDate: '',
        isPercent: false,
        description: '',
        discountType: '',
        productDiscounts: [],
        brandDiscounts: [],
        shopDiscounts: [],
        userDiscounts: [],
    });

    useEffect(() => {
        fetchDiscounts();
        getShops();
        getBrands();
        loadProducts();
        getUsers();
    }, []);


    const loadProducts = async () => {
        const res = await ProductService.getAllProductList();
        setProducts(res.data);
    }

    async function getUsers() {
        const params = {
            pageIndex: 1,
            pageSize: 1000,
        } as IUserGetParam
        const res = await UserService.getUserList(params) as any;
        if (res?.isSucceed) {
            setUsers(res.data.items);
        }
    }

    const getShops = () => {
        if (!authStore.user.role.includes("ADMIN")) {
            setShops(authStore.user.shops);
            return;
        }
        UserService.getShops().then(res => {
            if (res?.data) {
                setShops(res.data);
            }
        }).catch(error => {
            console.log(error);
        });
    }

    const getBrands = () => {
        InventoryService.getBrands({}).then(res => {
            if (res?.data) {
                setBrands(res.data);
            }
        }).catch(error => {
            console.log(error);
        });
    }

    const fetchDiscounts = async () => {
        const response = await api.post(SALES_API_URL.GET_DISCOUNTS, {
            userIds: filterUsers || [],
        });
        const data = response.data;
        setDiscounts(data);
    };

    const handleClose = () => {
        setFormData({
            discountId: -1,
            discountCode: '',
            discountValue: '',
            discountStock: '',
            discountOriginalStock: 0,
            startDate: '',
            endDate: '',
            isPercent: false,
            description: '',
            discountType: '',
            userDiscounts: [],
            productDiscounts: [],
            brandDiscounts: [],
            shopDiscounts: [],
        });
        setOpen(false);
    };

    const handleInputChange = (e: any) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value,
        });
    };

    const handleSubmit = async (e: any) => {
        e.preventDefault();
        const url = formData.discountId > -1
            ? SALES_API_URL.UPDATE_DISCOUNT
            : SALES_API_URL.CREATE_DISCOUNT;
        await api.post(url, formData) as any

        fetchDiscounts();
        handleClose();
    };

    const handleEdit = (discount: any) => {
        setFormData(discount);
        setOpen(true);
    };

    const handleDelete = async (discountId: number) => {
        await api.post(SALES_API_URL.DELETE_DISCOUNT + `?id=${discountId}`);
        fetchDiscounts();
        setDelAnchorEl(null);
    };

    const onChangeShops = (items: any[]) => {
        const _ids = items.map(_ => ({ shopId: _.id })) as any;
        setFormData({ ...formData, shopDiscounts: _ids });
    }

    const onChangeBrands = (items: any[]) => {
        const _ids = items.map(_ => ({ brandId: _.id })) as any;
        setFormData({ ...formData, brandDiscounts: _ids });
    }

    const onChangeProducts = (items: any[]) => {
        const _ids = items.map(_ => ({ productId: _.id })) as any;
        setFormData({ ...formData, productDiscounts: _ids });
    }

    const onChangeUsers = (items: any[]) => {
        const _ids = items.map(_ => ({ userId: _.userId })) as any;
        setFormData({ ...formData, userDiscounts: _ids });
    }

    const onChangeFilterUsers = (items: any[]) => {
        const _ids = items.map(_ => _.userId) as any;
        setFilterUsers(_ids);
    }

    return (
        <Box>
            {!open && (
                <>
                    <Grid container spacing={2} sx={{ marginBottom: 2 }}>
                        <Grid item xs={12} sm={4}>
                            <Autocomplete
                                style={{ marginTop: 10 }}
                                multiple
                                id="checkboxes-tags-demo"
                                options={users}
                                disableCloseOnSelect
                                getOptionLabel={(item: any) => `${item.userId} - ${item.userFullName}`}
                                value={
                                    users.length > 0 && filterUsers
                                        ? users.filter((item: any) => filterUsers.some((id: any) => id === item.userId)) 
                                        : []
                                }
                                renderOption={(props, item: any, { selected }) => (
                                    <li {...props}>
                                        <Checkbox
                                            icon={icon}
                                            checkedIcon={checkedIcon}
                                            style={{ marginRight: 8 }}
                                            checked={selected}
                                            size="small"
                                        />
                                        {item.userId} - {item.userFullName}
                                    </li>
                                )}
                                renderInput={(params) => (
                                    <TextField {...params} label="Người dùng" placeholder="Chọn người dùng" size="small" />
                                )}
                                onChange={(event, items: any) => onChangeFilterUsers(items)}
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Button variant="contained" style={{ marginRight: '1rem' }} onClick={() => fetchDiscounts()}>
                                Xem
                            </Button>
                            <Button variant="contained" onClick={() => setOpen(true)}>
                                Thêm Mới
                            </Button>
                        </Grid>
                    </Grid>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Mã Giảm Giá</TableCell>
                                    <TableCell>Giá Trị</TableCell>
                                    <TableCell>Số Lượng</TableCell>
                                    <TableCell>Ngày Bắt Đầu</TableCell>
                                    <TableCell>Ngày Kết Thúc</TableCell>
                                    <TableCell>Hành Động</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {discounts.map((discount: any) => (
                                    <TableRow key={discount.discountId}>
                                        <TableCell>{discount.discountCode}</TableCell>
                                        <TableCell>{discount.isPercent ? `${discount.discountValue} %` : ProductHelper.getFormatedPrice(discount.discountValue)}</TableCell>
                                        <TableCell>{discount.discountStock}/{discount.discountOriginalStock}</TableCell>
                                        <TableCell>{DateTimeHelper.getDateTimeFormated(discount.startDate)}</TableCell>
                                        <TableCell>{DateTimeHelper.getDateTimeFormated(discount.endDate)}</TableCell>
                                        <TableCell>
                                            <IconButton size="small" onClick={() => handleEdit(discount)}>
                                                <ModeEditIcon />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={(event) => setDelAnchorEl(event.currentTarget)}
                                            >
                                                <DeleteOutlineIcon />
                                            </IconButton>
                                            <Menu
                                                anchorEl={delAnchorEl}
                                                open={openDel}
                                                onClose={() => setDelAnchorEl(null)}
                                            >
                                                <MenuItem onClick={() => handleDelete(discount.discountId)}>
                                                    Xác Nhận Xóa
                                                </MenuItem>
                                                <MenuItem onClick={() => setDelAnchorEl(null)}>Hủy</MenuItem>
                                            </Menu>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}
            {open && (
                <Box component="form" onSubmit={handleSubmit}>
                    <Dialog open={open} onClose={handleClose}>
                        <DialogTitle>Chi Tiết Giảm Giá</DialogTitle>
                        <DialogContent>
                            <TextField
                                required
                                margin="dense"
                                name="discountCode"
                                label="Mã Giảm Giá"
                                fullWidth
                                variant="standard"
                                value={formData.discountCode}
                                onChange={handleInputChange}
                            />
                            {users.length > 0 && (
                                <Grid item xs={12} sm={12}>
                                    <Autocomplete
                                        style={{ marginTop: 10 }}
                                        multiple
                                        id="checkboxes-tags-demo"
                                        options={users}
                                        disableCloseOnSelect
                                        getOptionLabel={(item: any) => `${item.userId} - ${item.userFullName}`}
                                        value={users.length > 0 && formData?.userDiscounts ? users.filter((item: any) => formData?.userDiscounts.some((_: any) => _.userId === item.userId)) : []}
                                        renderOption={(props, item: any, { selected }) => (
                                            <li {...props}>
                                                <Checkbox
                                                    icon={icon}
                                                    checkedIcon={checkedIcon}
                                                    style={{ marginRight: 8 }}
                                                    checked={selected}
                                                    size="small"
                                                />
                                                {item.userId} - {item.userFullName}
                                            </li>
                                        )}
                                        renderInput={(params) => (
                                            <TextField {...params} label="Người dùng" placeholder="Chọn người dùng" size="small" />
                                        )}
                                        onChange={(event, items: any) => onChangeUsers(items)}
                                        size="small"
                                    />
                                </Grid>
                            )}
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        name="isPercent"
                                        checked={formData.isPercent}
                                        onChange={(e) => handleInputChange(e)}
                                    />
                                }
                                label="Giảm theo phần trăm"
                            />
                            <TextField
                                required
                                margin="dense"
                                name="discountValue"
                                label="Giá Trị"
                                fullWidth
                                variant="standard"
                                value={formData.discountValue}
                                onChange={handleInputChange}
                            />
                            <TextField
                                margin="dense"
                                type='number'
                                name="discountOriginalStock"
                                label="Số lượng ban đầu"
                                fullWidth
                                variant="standard"
                                value={formData.discountOriginalStock}
                                onChange={handleInputChange}
                            />
                            <TextField
                                margin="dense"
                                type='number'
                                name="discountStock"
                                label="Số Lượng"
                                fullWidth
                                variant="standard"
                                value={formData.discountStock}
                                onChange={handleInputChange}
                            />
                            <TextField
                                margin="dense"
                                name="startDate"
                                label="Ngày Bắt Đầu"
                                type="date"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                value={formData.startDate ? formData.startDate.split("T")[0] : null}
                                onChange={handleInputChange}
                            />
                            <TextField
                                margin="dense"
                                name="endDate"
                                label="Ngày Kết Thúc"
                                type="date"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                value={formData.endDate ? formData.endDate.split("T")[0] : null}
                                onChange={handleInputChange}
                            />
                            {shops.length > 0 ? (
                                <Grid item xs={12} sm={12}>
                                    <Autocomplete
                                        style={{ marginTop: 10 }}
                                        multiple
                                        id="checkboxes-tags-demo"
                                        options={shops}
                                        disableCloseOnSelect
                                        getOptionLabel={(item: any) => `${item.id} - ${item.name}`}
                                        value={shops.length > 0 && formData?.shopDiscounts ? shops.filter((item: any) => formData?.shopDiscounts.some((_: any) => _.shopId === item.id)) : []}
                                        renderOption={(props, item: any, { selected }) => (
                                            <li {...props}>
                                                <Checkbox
                                                    icon={icon}
                                                    checkedIcon={checkedIcon}
                                                    style={{ marginRight: 8 }}
                                                    checked={selected}
                                                    size="small"
                                                />
                                                {item.id} - {item.name}
                                            </li>
                                        )}
                                        renderInput={(params) => (
                                            <TextField {...params} label="Cửa hàng" placeholder="Chọn cửa hàng" size="small" />
                                        )}
                                        onChange={(event, items: any) => onChangeShops(items)}
                                        size="small"
                                    />
                                </Grid>
                            ) : null}
                            {brands.length > 0 && (
                                <Grid item xs={12} sm={12}>
                                    <Autocomplete
                                        style={{ marginTop: 10 }}
                                        multiple
                                        id="checkboxes-tags-demo"
                                        options={brands}
                                        disableCloseOnSelect
                                        getOptionLabel={(item: any) => `${item.id} - ${item.name}`}
                                        value={brands.length > 0 && formData?.brandDiscounts ? brands.filter((item: any) => formData?.brandDiscounts.some((_: any) => _.brandId === item.id)) : []}
                                        renderOption={(props, item: any, { selected }) => (
                                            <li {...props}>
                                                <Checkbox
                                                    icon={icon}
                                                    checkedIcon={checkedIcon}
                                                    style={{ marginRight: 8 }}
                                                    checked={selected}
                                                    size="small"
                                                />
                                                {item.id} - {item.name}
                                            </li>
                                        )}
                                        renderInput={(params) => (
                                            <TextField {...params} label="Thương hiệu" placeholder="Chọn thương hiệu" size="small" />
                                        )}
                                        onChange={(event, items: any) => onChangeBrands(items)}
                                        size="small"
                                    />
                                </Grid>
                            )}
                            {products.length > 0 && (
                                <Grid item xs={12} sm={12}>
                                    <Autocomplete
                                        style={{ marginTop: 10 }}
                                        multiple
                                        id="checkboxes-tags-demo"
                                        options={products}
                                        disableCloseOnSelect
                                        getOptionLabel={(item: any) => `${item.id} - ${item.name}`}
                                        value={products.length > 0 && formData?.productDiscounts ? products.filter((item: any) => formData?.productDiscounts.some((_: any) => _.productId === item.id)) : []}
                                        renderOption={(props, item: any, { selected }) => (
                                            <li {...props}>
                                                <Checkbox
                                                    icon={icon}
                                                    checkedIcon={checkedIcon}
                                                    style={{ marginRight: 8 }}
                                                    checked={selected}
                                                    size="small"
                                                />
                                                {item.id} - {item.name}
                                            </li>
                                        )}
                                        renderInput={(params) => (
                                            <TextField {...params} label="Sản phẩm" placeholder="Chọn sản phẩm" size="small" />
                                        )}
                                        onChange={(event, items: any) => onChangeProducts(items)}
                                        size="small"
                                    />
                                </Grid>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleClose}>Hủy</Button>
                            <Button onClick={handleSubmit}>Lưu</Button>
                        </DialogActions>
                    </Dialog>
                </Box>
            )}
        </Box>
    );
};